import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Collapse,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  CircularProgress,
} from "components/mui";
import { AddIcon, RemoveIcon, DeleteIcon, ContentCopyIcon, DownloadIcon } from "components/icons";
import MailingListEditor from "../mailingListEditor/MailingListEditor";
import CloneListModal from "../mailingListEditor/CloneListModal";
import { getComparator, stableSort } from "libs/sortList";
import { useNotificationProvider } from "hooks";
import { exportMailListToCsv } from "utils/canmail/exportMailListCsv";

const MailingListRow = ({ list, setMailingLists, onDelete, onListUpdated, existingNames, onCloned }) => {
  const [expanded, setExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { notifySuccess, notifyError, notifyInfo } = useNotificationProvider();

  const handleExport = async () => {
    setExporting(true);
    try {
      const count = await exportMailListToCsv(list);
      if (count > 0) {
        notifySuccess(`Exported ${count} contact${count === 1 ? "" : "s"}.`);
      } else {
        notifyInfo("This list has no contacts to export.");
      }
    } catch (error) {
      console.error("[MailingListRow] Failed to export list:", error);
      notifyError("Failed to export the mailing list. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (onDelete) {
      await onDelete(list.list_id);
    }
    setShowDeleteModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <TableRow>
        <TableCell sx={{ py: 1 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <RemoveIcon /> : <AddIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 1 }}>{list.list_name}</TableCell>
        <TableCell align="right" sx={{ py: 1, whiteSpace: "nowrap" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
            <Tooltip title="Export CSV">
              <span>
                <IconButton
                  size="small"
                  onClick={handleExport}
                  color="primary"
                  disabled={exporting}
                >
                  {exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Clone list">
              <IconButton
                size="small"
                onClick={() => setShowCloneModal(true)}
                color="primary"
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete list">
              <IconButton
                size="small"
                onClick={handleDeleteClick}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <MailingListEditor 
                list={list} 
                setMailingLists={setMailingLists}
                onClose={() => setExpanded(false)}
                onListUpdated={onListUpdated}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title">
          Delete Mailing List
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the mailing list "{list.list_name}"? 
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCancelDelete}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone List Modal */}
      <CloneListModal
        open={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        sourceList={list}
        existingNames={existingNames}
        onCloned={onCloned}
      />
    </>
  );
};

const MailingListTable = ({ mailingLists, setMailingLists, onDelete, onListUpdated, onCloned }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("list_name");

  // Names of all existing lists, used to block cloning to a duplicate name.
  const existingNames = mailingLists.map((list) => list.list_name);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedLists = stableSort(
    mailingLists,
    getComparator(order, orderBy)
  );

  return (
    <Paper elevation={1}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} sx={{ py: 1 }} />
              <TableCell sx={{ py: 1 }}>
                <TableSortLabel
                  active={orderBy === "list_name"}
                  direction={orderBy === "list_name" ? order : "asc"}
                  onClick={() => handleSort("list_name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width={50} sx={{ py: 1 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedLists.map((list) => (
              <MailingListRow
                key={list.list_id}
                list={list}
                setMailingLists={setMailingLists}
                onDelete={onDelete}
                onListUpdated={onListUpdated}
                existingNames={existingNames}
                onCloned={onCloned}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// PropTypes for type checking
MailingListTable.propTypes = {
  mailingLists: PropTypes.array,
  setMailingLists: PropTypes.func,
  onDelete: PropTypes.func,
  onListUpdated: PropTypes.func,
  onCloned: PropTypes.func,
};

export default MailingListTable;
